/**
 * Which speciality this session READS AS — one answer, for every surface that asks.
 *
 * Three readers can differ from the account's own row, and the order matters:
 *
 *  1. « Vue médecin » — the previewed doctor answers for themselves, their speciality included,
 *     and including when their row carries none. Falling back to the admin's own speciality there
 *     would read one practitioner's dossiers through another speciality's dossier, which is the
 *     whole class of bug a second speciality made visible.
 *  2. « Vue spécialité » — the speciality an admin chose from the sidebar.
 *  3. Otherwise, the speciality on the reader's own `User` row.
 *
 * The sidebar's own label is the first consumer that matters to a reader: the eye must dominate
 * every other affordance, so what the picker DISPLAYS is this, not the picker's own memory.
 */
import { type PreviewMedecin, usePreviewView } from "@app/zustand/preview-view";
import useUser from "@app/zustand/user";

/**
 * The account « Vue médecin » reproduces, or `null` when the session reads as itself.
 *
 * `previewRole === "medecin"` alone does not mean another account is on screen: the demo sets that
 * role on ITSELF at login, as a persona, with no doctor named. Reading that as a preview resolved
 * the demo through an account that does not exist, with no speciality.
 */
export function usePreviewedAccount(): PreviewMedecin | null {
  const previewRole = usePreviewView((state) => state.previewRole);
  const previewMedecin = usePreviewView((state) => state.previewMedecin);
  return previewRole === "medecin" ? previewMedecin : null;
}

export function useEffectiveSpecialite(): string | null | undefined {
  const user = useUser((state) => state.user);
  const previewedAccount = usePreviewedAccount();
  const previewSpecialite = usePreviewView((state) => state.previewSpecialite);
  if (previewedAccount) return previewedAccount.specialite ?? null;
  return (
    previewSpecialite ??
    (user as { specialite?: string | null } | null)?.specialite
  );
}
